import { useEffect } from "react";
import { CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";

interface CompletionProps {
  userName: string;
  onViewMatches: () => void;
}

const OnboardingCompletion = ({ userName, onViewMatches }: CompletionProps) => {
  useEffect(() => {
    // Trigger confetti animation
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 100 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);
      
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#8B5CF6', '#D946EF', '#F97316', '#10B981'],
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#8B5CF6', '#D946EF', '#F97316', '#10B981'],
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  const firstName = userName.split(" ")[0] || "there";

  return (
    <div className="text-center space-y-8 py-8">
      {/* Success Icon */}
      <div className="relative inline-flex">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center animate-scale-in">
          <CheckCircle2 className="h-12 w-12 text-white" />
        </div>
        <div className="absolute -top-2 -right-2">
          <Sparkles className="h-8 w-8 text-amber-400 animate-pulse" />
        </div>
      </div>

      {/* Message */}
      <div className="space-y-3">
        <h2 className="text-3xl font-bold text-foreground">
          You're all set, {firstName}!
        </h2>
        <p className="text-lg text-muted-foreground max-w-sm mx-auto">
          Your preferences are saved. Explore brokerages to see how each one
          fits you, and add more of what you're looking for on your profile to
          sharpen those scores.
        </p>
      </div>

      {/* No preview scores or counts here: this screen has no real results
          to show, and made-up numbers would promise matches that don't exist. */}

      {/* CTA */}
      <Button
        size="lg"
        onClick={onViewMatches}
        className="gap-2 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
      >
        Start exploring
        <ArrowRight className="h-5 w-5" />
      </Button>
    </div>
  );
};

export default OnboardingCompletion;
